<template>
  <div :style="fullWindow4to3RatioDimensionStyle" class="relative">
    <DailyTimelapseContent
      :enableHeaderDateField="true"
      :date="date"
      :timelapse="timelapse"
      :loading="loading"
      class="h-full"
      :noSrcMessage="noSrcMessage"
      @dateChange="handleDateChange"
    />
    <LogoWatermark class="absolute top-2 right-2" />
  </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { format, parseISO } from "date-fns";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import DailyTimelapseContent from "shared/components/camera/DailyTimelapseContent.vue";
import LogoWatermark from "shared/components/other/LogoWatermark.vue";
import { useCurrentCustomerName, useCurrentSiteId } from "shared/composables/project";
import { useFullWindow4to3RatioDimensionStyle } from "shared/composables/screen";
import { useCustomToast } from "shared/composables/toast";
import CameraRepository from "shared/repositories/CameraRepository";
import PublicTokenRepository from "shared/repositories/PublicTokenRepository";
import logger from "shared/services/logger";
import { DailyTimelapseResponse } from "shared/types/Camera";

const { t } = useI18n();

const timelapse = ref<DailyTimelapseResponse | null>(null);
const date = ref<Date | null>(null);
const loading = ref(false);

const route = useRoute();
const toast = useCustomToast();
const currentCustomerName = useCurrentCustomerName();
const currentSiteId = useCurrentSiteId();
const noSrcMessage = ref<string | undefined>(undefined);

const fullWindow4to3RatioDimensionStyle = useFullWindow4to3RatioDimensionStyle({
  horizontal: 0,
  vertical: 40,
});

onMounted(() => {
  loadDailyTimelapse(dateToString.value);
});

const dateToString = computed(() => (date.value ? format(date.value, "yyyy-MM-dd") : ""));

const loadDailyTimelapse = async (value: string | null | undefined) => {
  loading.value = true;
  const { camera_id } = route.params;

  try {
    noSrcMessage.value = undefined;
    date.value = value ? parseISO(value) : null;
    loading.value = true;
    timelapse.value = await CameraRepository.loadDailyTimelapse(
      currentCustomerName,
      currentSiteId,
      camera_id as string,
      value || null,
    );

    if (timelapse.value) {
      date.value = new Date(timelapse.value.date);
    }
  } catch (error) {
    const is404 = axios.isAxiosError(error) && error.response?.status === 404;
    if (!PublicTokenRepository.isPublicTokenAuthError(error) && !is404) {
      logger.error(error);
    }
    timelapse.value = null;
    if (PublicTokenRepository.isPublicTokenAuthError(error)) {
      noSrcMessage.value = t("public_tokens.link_expired");
    } else {
      toast.warning(t("err.no_data_found"));
    }
  } finally {
    loading.value = false;
  }
};

const handleDateChange = (date: string | null) => {
  loadDailyTimelapse(date);
};
</script>
